import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr } from "@/lib/api";
import { LOCATIONS } from "@/data/content";

const today = () => new Date().toISOString().slice(0, 10);

export const BookingForm = ({ pkg, slots }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ location: LOCATIONS[0].name, date: today(), slot: "", guests: 2, name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const perLane = pkg?.unit === "per lane";
  const total = pkg ? (perLane ? pkg.price : pkg.price * f.guests) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!pkg) return;
    if (!f.slot) { toast.error("Please pick a time slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { kind: "package", package_id: pkg.id, ...f, guests: Number(f.guests) });
      nav(`/booking/success?code=${data.code}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="rounded-[28px] bg-[#0E101D] border border-white/10 p-6 md:p-9">
      <div className="flex items-center justify-between gap-4 pb-6 border-b border-white/10">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Selected</p>
          <p className="font-display font-extrabold text-xl md:text-2xl text-[#FFEF00]" data-testid="booking-selected-package">{pkg?.name || "—"}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Total</p>
          <p className="font-display font-black text-2xl md:text-3xl" data-testid="booking-total">{inr(total)}</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <select data-testid="booking-location-select" className="field" value={f.location} onChange={set("location")}>
          {LOCATIONS.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
        </select>
        <input data-testid="booking-date-input" type="date" min={today()} className="field" value={f.date} onChange={set("date")} required />
      </div>
      <p className="mt-6 mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Time slot</p>
      <div className="flex flex-wrap gap-2">
        {slots.map((s) => (
          <button type="button" key={s} data-testid={`booking-slot-${s.replace(":", "")}`} onClick={() => setF({ ...f, slot: s })}
            className={`rounded-full px-4 py-2 text-sm font-mono border-2 transition-colors ${f.slot === s ? "bg-[#14C3F7] text-black border-black" : "border-white/15 hover:border-[#14C3F7]"}`}>{s}</button>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between rounded-2xl border border-white/10 px-4 py-3">
        <span className="text-sm text-slate-300">Guests {perLane && <span className="text-slate-400">(lane fits up to 6)</span>}</span>
        <div className="flex items-center gap-4">
          <button type="button" data-testid="booking-guests-minus" onClick={() => setF({ ...f, guests: Math.max(1, f.guests - 1) })} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#FFEF00]"><Minus size={16} /></button>
          <span className="font-display font-bold w-6 text-center" data-testid="booking-guests-count">{f.guests}</span>
          <button type="button" data-testid="booking-guests-plus" onClick={() => setF({ ...f, guests: Math.min(60, f.guests + 1) })} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:border-[#FFEF00]"><Plus size={16} /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <input data-testid="booking-name-input" className="field" placeholder="Full name" value={f.name} onChange={set("name")} required minLength={2} />
        <input data-testid="booking-phone-input" className="field" placeholder="Phone" value={f.phone} onChange={set("phone")} required minLength={7} />
        <input data-testid="booking-email-input" type="email" className="field" placeholder="Email" value={f.email} onChange={set("email")} required />
      </div>
      <button data-testid="booking-submit-btn" disabled={busy || !pkg} className="btn-zoop w-full mt-8 rounded-full py-4 font-display font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy && <Loader2 className="animate-spin" size={18} />} Confirm booking · {inr(total)}
      </button>
      <p className="mt-3 text-center text-xs text-slate-400">Pay at the venue on arrival. Online payment coming soon.</p>
    </form>
  );
};
